// Balance constants. Every number here is referenced in GAME_DESIGN_DOCUMENT.md section 12.

export const TIME = {
  /** 3 real seconds = 1 game minute */
  REAL_SECONDS_PER_GAME_MINUTE: 3,
  /** Clock multiplier while a task panel is open */
  FOCUS_TIME_SCALE: 4,
  /** 06:00 */
  DAY_START: 6 * 60,
  /** 8 hour work day */
  WORK_MINUTES: 8 * 60,
  /** Overtime allowed after the work day before forced sleep */
  OVERTIME_MINUTES: 4 * 60,
  DAYS_PER_WEEK: 7,
  AUTOSAVE_REAL_SECONDS: 10,
} as const;

export const ENERGY = {
  MAX: 100,
  PASSIVE_DRAIN_PER_HOUR: 5,
  OVERTIME_DRAIN_MULT: 2,
  OVERTIME_PENALTY_PER_HOUR: 5,
  LOW_THRESHOLD: 25,
  LOW_TOLERANCE_MULT: 0.6,
  EAT_BASE: 10,
  EAT_PER_STAR: 4,
  EAT_WHEY: 4,
} as const;

/**
 * Skills are techniques shared across cheeses, so mastery carries over:
 * pasteurize covers every heating step, cut covers cutting, skimming and molding,
 * culture covers ripening with starter cultures, brine covers salting, cellar covers
 * affinage (washing rinds, tending the racks).
 */
export type TaskId =
  | 'feed'
  | 'milk'
  | 'pasteurize'
  | 'curdle'
  | 'culture'
  | 'rennet'
  | 'cut'
  | 'stretch'
  | 'brine'
  | 'cellar'
  | 'bag'
  | 'sell';

export const TASK_IDS: TaskId[] = ['feed', 'milk', 'pasteurize', 'curdle', 'culture', 'rennet', 'cut', 'stretch', 'brine', 'cellar', 'bag', 'sell'];

export interface TaskDef {
  label: string;
  energy: number;
  /** Fixed game minutes added when the task completes (0 = runs on the clock) */
  minutes: number;
}

export const TASK: Record<TaskId, TaskDef> = {
  feed: { label: 'Feeding', energy: 1, minutes: 5 },
  milk: { label: 'Milking', energy: 10, minutes: 15 },
  pasteurize: { label: 'Heating & Pasteurizing', energy: 3, minutes: 0 },
  curdle: { label: 'Acid Curdling', energy: 5, minutes: 10 },
  culture: { label: 'Culturing', energy: 2, minutes: 0 },
  rennet: { label: 'Renneting', energy: 2, minutes: 0 },
  cut: { label: 'Cutting & Draining', energy: 5, minutes: 10 },
  stretch: { label: 'Stretching', energy: 8, minutes: 10 },
  brine: { label: 'Brining', energy: 3, minutes: 10 },
  cellar: { label: 'Affinage', energy: 3, minutes: 10 },
  bag: { label: 'Packaging', energy: 1, minutes: 5 },
  sell: { label: 'Selling', energy: 0, minutes: 0 },
};

export const MILK = {
  YIELD_MIN: 3.0,
  YIELD_MAX: 5.0,
  LEVEL_YIELD_BONUS: 0.2,
  SCORE_YIELD_BASE: 0.85,
  SCORE_YIELD_RANGE: 0.15,
  SCORE_QUALITY_BONUS: 0.5,
  COOLDOWN_MINUTES: 4 * 60,
  /** Milkings Bessie gives per day (work day ÷ cooldown) */
  MILKINGS_PER_DAY: 2,
  VAT_CAPACITY_GAL: 6,
  PULLS: 3,
  FEED: {
    basic: { qualityBase: 2.5, cream: 3.5, creamVar: 0.3 },
    quality: { qualityBase: 3.8, cream: 4.2, creamVar: 0.3 },
  },
} as const;

export const PASTEURIZE = {
  TARGET: 145,
  BAND_LOW: 143,
  BAND_HIGH: 147,
  SCORCH_TEMP: 150,
  SCORCH_PENALTY_PER_MIN: 0.03,
  HOLD_MINUTES: 30,
  OVERHOLD_PENALTY_PER_MIN: 0.02,
  /** Milk is considered "still hot" above this after the hold completes */
  OVERHOLD_TEMP: 140,
  AMBIENT: 60,
  HEAT_RATE: { off: 0, low: 1.5, high: 3.0 } as Record<'off' | 'low' | 'high', number>,
  COOL_RATE_OFF: 0.5,
  /** Thermal lag: fraction of the gap between target rate and current rate closed per minute.
   *  Expected drift after a control change is roughly currentRate / LAG degrees. */
  LAG: 0.7,
  START_TEMP: 60,
} as const;

export const CURDLE = {
  BAND_LOW: 88,
  BAND_HIGH: 90,
  OUT_OF_BAND_PENALTY_PER_F: 0.05,
  COOL_RATE: 3,
  BEATS: 8,
  BEAT_MS: 800,
  TOLERANCE_MS: 180,
  MISS_PENALTY: 0.1,
  /** Minutes the cooled milk may sit at temperature before acid is added */
  MAX_WAIT_MINUTES: 10,
  OVERTIME_PENALTY: 0.1,
} as const;

export const RENNET = {
  STIR_REAL_SECONDS: 2,
  SET_MIN: 5,
  SET_MAX: 10,
  EARLY_YIELD_PENALTY_PER_MIN: 0.1,
  LATE_QUALITY_PENALTY_PER_MIN: 0.05,
} as const;

export const CUT = {
  SLICES: 4,
  WHEY_PER_GALLON: 0.85,
  BEATS: 6,
  BEAT_MS: 1400,
  TOLERANCE_MS: 250,
  EARLY_YIELD_PENALTY: 0.05,
} as const;

export const STRETCH = {
  TARGET_TEMP: 170,
  OVERHEAT_TEMP: 180,
  OVERHEAT_PENALTY: 0.1,
  HEAT_RATE: 5,
  PULLS: 3,
  MISS_PENALTY: 0.1,
  ZONE_START: 0.6,
  ZONE_END: 0.85,
  FILL_MS: 1500,
} as const;

/** Whey ricotta: reheat the whey, acidify, rest, skim, drain, tub. */
export const RICOTTA = {
  MIN_GALLONS: 2,
  START_TEMP: 60,
  TARGET: 195,
  BAND_LOW: 190,
  BAND_HIGH: 200,
  SCORCH_TEMP: 205,
  SCORCH_PENALTY_PER_MIN: 0.03,
  OUT_OF_BAND_PENALTY_PER_F: 0.04,
  /** Undisturbed rest after the acid, in game minutes */
  REST_MIN: 10,
  REST_MAX: 20,
  EARLY_REST_YIELD_PER_MIN: 0.06,
  LATE_REST_QUALITY_PER_MIN: 0.02,
  SKIM_SCOOPS: 3,
  SKIM_MISS_YIELD: 0.08,
  SKIM_PERIOD_MS: 1400,
  SKIM_ZONE: 0.24,
  /** Minimum draining time before the curd can be tubbed */
  DRAIN_MIN: 20,
  EARLY_DRAIN_PENALTY: 0.3,
  /** Pounds of ricotta per gallon of whey */
  LB_PER_GAL: 0.6,
} as const;

/** Taleggio: washed-rind soft cheese, drained in square molds, brined, cellar aged. */
export const TALEGGIO = {
  CULTURE_TARGET: 92,
  CULTURE_BAND_LOW: 90,
  CULTURE_BAND_HIGH: 93,
  OUT_OF_BAND_PENALTY_PER_F: 0.05,
  /** Ripening window after the culture goes in, game minutes */
  RIPEN_MIN: 30,
  RIPEN_MAX: 45,
  RIPEN_EARLY_PENALTY_PER_MIN: 0.02,
  RIPEN_LATE_PENALTY_PER_MIN: 0.02,
  SET_MIN: 20,
  SET_MAX: 30,
  EARLY_YIELD_PENALTY_PER_MIN: 0.04,
  LATE_QUALITY_PENALTY_PER_MIN: 0.03,
  SLICES: 4,
  WHEY_PER_GALLON: 0.8,
  /** Draining in molds: total minutes and how often the molds must be turned */
  DRAIN_MINUTES: 60,
  TURN_EVERY: 20,
  MISSED_TURN_PENALTY: 0.1,
  BRINE_MIN_HOURS: 4,
  BRINE_MAX_HOURS: 8,
  UNDER_BRINE_PENALTY_PER_HOUR: 0.1,
  OVER_BRINE_PENALTY_PER_HOUR: 0.05,
  /** Pounds per square */
  UNIT_LB: 1,
} as const;

export const YIELD = {
  LB_PER_GAL: 1.0,
  CREAM_REF: 3.5,
  CREAM_DIV: 5,
  MIN_FACTOR: 0.4,
} as const;

export const VAT = {
  DURABILITY_MAX: 100,
  WEAR_PER_BATCH: 8,
  WORN_THRESHOLD: 20,
  WORN_PENALTY: 0.5,
  REPAIR_COST: 30,
} as const;

/** Aging cellar. Temperatures in °F, humidity in percent. */
export const CELLAR = {
  TEMP_LOW: 50,
  TEMP_HIGH: 57,
  HUM_LOW: 85,
  HUM_HIGH: 92,
  START_TEMP: 55,
  START_HUM: 80,
  /** Daily random drift of the room */
  DRIFT_TEMP: 2,
  DRIFT_HUM: 3,
  /** Aging days gained per calendar day: both dials in range, one in range, none */
  SPEED_OPTIMAL: 1.25,
  SPEED_HALF: 1.0,
  SPEED_POOR: 0,
  /** Consecutive stalled days before a rack spoils */
  STALL_SPOIL_DAYS: 5,
  /** Washed-rind cheeses want a brine wash this often (aging days) */
  WASH_EVERY_DAYS: 5,
  WASH_GRACE_DAYS: 3,
  /** Each missed wash takes this off the aging value multiplier */
  MISSED_WASH_VALUE_PENALTY: 0.1,
  RACK_CAPACITY: 12,
  /** Salt used per wash of the whole cellar */
  SALT_PER_WASH: 1,
} as const;

export const MARKET = {
  DILUTION_FLOOR: 0.4,
  SHOP_CHANNEL: 1.35,
  NEGOTIATION_PER_SKILL: 0.05,
  /** Pounds of dry whey solids in a gallon of liquid whey */
  WHEY_DRY_LB_PER_GALLON: 0.55,
  /** Fallback depth if the snapshot lacks a value */
  DEFAULT_DEPTH: 60,
} as const;

export const GEMS = {
  /** Drop chance by stars (index = stars) */
  CHANCE_BY_STARS: [0, 0, 0.05, 0.1, 0.15, 0.2] as const,
  RARITY_MULT: { common: 1, uncommon: 2, rare: 3, epic: 4, legendary: 5 } as Record<string, number>,
} as const;

export const XP = {
  LEVEL_DIVISOR: 50,
  TOLERANCE_PER_LEVEL: 0.05,
  TOLERANCE_CAP: 0.5,
  RELAXED_TIMING_MULT: 1.5,
  STEADY_HANDS_BONUS: 0.1,
  /** Mastery of a process is this task level (xp = 50 × level²) */
  MASTERY_LEVEL: 6,
  /** After mastery, XP goes to the training bank and slows as the bank grows */
  BANK_SOFT_CAP: 1000,
  /** Fraction of the bank handed to each assigned employee per day */
  BANK_TRANSFER_PER_DAY: 0.02,
  /** Fraction of a trainer's XP passed to each junior in that skill per day */
  TRAINER_TRANSFER_PER_DAY: 0.01,
} as const;

export const MASTERY_XP = XP.LEVEL_DIVISOR * XP.MASTERY_LEVEL * XP.MASTERY_LEVEL;

/** The Mouse Codex of Cheese */
export const CODEX = {
  QUESTIONS: 5,
  /** Correct answers in one attempt needed to learn the recipe */
  PASS_CORRECT: 5,
  RETEST_COST: 50,
  GEM_PER_FIRST_CORRECT: 1,
  /** Discovery fee by chapter */
  FEES: { soft: 100, semi_soft: 150, semi_hard: 200, hard: 250, blue: 350, mystery: 500 } as Record<string, number>,
} as const;

/** Employees */
export const STAFF = {
  BASE_WAGE: 6,
  /** Gold per day per XP point the employee arrived with */
  WAGE_PER_HIRED_XP: 0.003,
  /** Gold per day per XP point earned under your roof (cheaper) */
  WAGE_PER_TRAINED_XP: 0.0015,
  TRAINER_WAGE_MULT: 1.5,
  SIGNING_FEE_DAYS: 3,
  CANDIDATES: 3,
  CANDIDATE_REFRESH_DAYS: 7,
  HIRED_XP_MAX_PER_TASK: 1200,
  /** Quality lost per stage at zero skill (scaled by 1 − skill) */
  STAGE_PENALTY: 0.3,
  MISTAKE_BASE: 0.25,
  MISTAKE_PENALTY: 0.2,
  HYGIENE_QUALITY_PER_POINT: 0.05,
  XP_PER_STAGE: 12,
  /** Expected wage drifts up with XP; paying less than this share of it counts as mismatch */
  QUIT_CHECK_MISMATCH: 0.1,
  QUIT_RATE: 0.2,
  ATTEND_BASE: 0.55,
  ATTEND_PER_LOYALTY: 0.045,
  ATTEND_MISMATCH_PENALTY: 0.4,
  ATTEND_MIN: 0.3,
} as const;

export type SupplyId = 'feed_basic' | 'feed_quality' | 'citric_acid' | 'rennet' | 'culture' | 'salt' | 'packaging';

export interface SupplyDef {
  label: string;
  price: number;
  unit: string;
  requiresSkill?: string;
}

export const SUPPLIES: Record<SupplyId, SupplyDef> = {
  feed_basic: { label: 'Basic feed', price: 2, unit: 'feeding' },
  feed_quality: { label: 'Quality feed', price: 6, unit: 'feeding', requiresSkill: 'quality_feed_supplier' },
  citric_acid: { label: 'Citric acid', price: 1, unit: 'batch' },
  rennet: { label: 'Rennet', price: 1, unit: 'batch' },
  culture: { label: 'Starter culture', price: 2, unit: 'batch' },
  salt: { label: 'Brine salt', price: 1, unit: 'batch or wash' },
  packaging: { label: 'Packaging', price: 0.25, unit: 'unit' },
};

export const START = {
  GOLD: 50,
  GEMS: 0,
  ENERGY: 100,
  SUPPLIES: { feed_basic: 5, feed_quality: 0, citric_acid: 3, rennet: 3, culture: 0, salt: 0, packaging: 12 } as Record<SupplyId, number>,
} as const;

export const SAVE = {
  KEY: 'cosmic-curds-save',
  VERSION: '2.4.0',
} as const;

export const WORLD = {
  WIDTH: 1280,
  HEIGHT: 720,
  TILE_W: 128,
  TILE_H: 64,
  STEP_MS: 180,
  /** Diagonal steps cover √2 tiles, so they take longer to keep world speed constant */
  DIAG_STEP_MS: 255,
} as const;
